import {
  BaseEquipment, BenchAnchorPoint, BenchOption, Equipment, EquipmentWeight,
  GymTypes, SmallBodyGroupType,
} from 'fitbeat.models';
import { AreaCategory } from '../../config/zone-mappings';
import { UserExerciseSession } from '../workoutsession';

// Deprecated! Use models/src/user/level.ts instead
export enum Level {
  Basic = 'Basic', // Intermediate in app
  Intermediate = 'Intermediate', // Advanced in app
  Expert = 'Expert', // not used in app currently
  Beginner = 'Beginner', // Basic in app
}

export interface IEquipmentWeightInfo {
  equipmentWeight: EquipmentWeight;
  equipment: Equipment;
  color?: string;
  description: string;
}

export enum CardioOrResistance {
  Cardio = 'Cardio',
  Resistance = 'Resistance',
  Balance = 'Balance',
}

export { ActivityType } from 'fitbeat.models';

export interface IVoiceTipReviewStatus {
  ptName: string;
  passed: boolean;
}

export enum BigBodyGroup {
  Upper = 'Upper',
  Lower = 'Lower',
  BackAndArms = 'Back & Arms',
}

export const SmallBodyGroup: { [key: string]: string } = {
  Shoulders: 'Shoulders',
  Triceps: 'Triceps',
  Chest: 'Chest',
  Abdomen: 'Abdomen',
  GlutesAndHips: 'Glutes & Hips',
  UpperLegs: 'Upper Legs',
  Biceps: 'Biceps',
  UpperBackAndTraps: 'Upper Back & Traps',
  LowerBackAndLats: 'Lower Back & Lats',
  LowerArms: 'Lower Arms',
  LowerLegs: 'Lower Legs',
};

export enum ExerciseFormat {
  StudioEmpty = 'StudioEmpty',
  Burn2Endurance = 'Burn2: Endurance',
  Build1FlashCombos = 'Build1: Flash Combos',
  Build2AmrapSets = 'Build2: Amrap Sets',
  Build3BoxingEndurance = 'Build3: Boxing Endurance',
  Burn1FlashCombos = 'Burn1: Flash Combos',
  MachineTrain = 'Machine Train',
  Warmup50Stretch = 'Warmup50Stretch',
  Warmup50Pace = 'Warmup50Pace',
  Warmdown50Pace = 'Warmdown50Pace',
  Warmdown50Stretch = 'Warmdown50Stretch',
  Warmup30Hybrid = 'Warmup30Hybrid',
  Warmdown30Hybrid = 'Warmdown30Hybrid',
  HeroZonePunches = 'Hero Zone Punches',
  DanceMove = 'Dance Move',
  HomeBreakBurn = 'Home Break Burn',
  HomeBreakBuild = 'Home Break Build',
  HomeBreakBalance = 'Home Break Balance',
  PauseAtWorkoutStarting = 'PauseAtWorkoutStarting',
  PauseAtHalfwayBreak = 'PauseAtHalfwayBreak',
  WarmupLowBuild = 'WULowBuild',
  WarmupModerateBuild = 'WUModBuild',
  WarmupHybridBuild = 'WUBuildHybrid',
  WarmupLowBurn = 'WULowBurn',
  WarmupModerateBurn = 'WUModBurn',
  WarmupHybridBurn = 'WUBurnHybrid',
  WarmdownBuild = 'WDBuild',
  WarmdownBurn = 'WDBurn',
  MachineSetupAndStretch = 'MachineSetupAndStretch',
  MachineEndAndStretch = 'MachineEndAndStretch',
  StaticFunctional = 'StaticFunctional',
  BurnTripple3Min = 'Burn Tripple 3 Min',
  BuildTripple3Min = 'Build Tripple 3 Min',
  BurnTripple3Min20Sec = 'Burn Tripple 3 Min 20 Sec',
  BuildTripple3Min20Sec = 'Build Tripple 3 Min 20 Sec',
  BurnDouble3Min = 'Burn Double 3 Min',
  BurnDoubleDouble3Min = 'Burn Double Double 3 Min', // only 2 exercises but 4 phases

  HomeBalanceQuad = 'Quad Balance',
  BalanceFlashCombo = 'Balance Flash Combo',
  WUModBalance = 'WUModBalance',
  WDBalance = 'WDBalance',
  WUBalanceHybrid = 'WUBalanceHybrid',
  WULowBalance = 'WULowBalance',
  WDBalanceStudio = 'WDBalanceStudio',
  MotiveBalanceQ1 = 'Motiv Balance Q1',
  MotiveBalanceQ2 = 'Motiv Balance Q2',
  MotiveBalanceQ3 = 'Motiv Balance Q3',

  BuildDouble3Min = 'Build Double 3 Min',
  BuildDoubleDouble4Min = 'Build Double Double 4 Min',
  BuildDoubleDouble3Min = 'Build Double Double 3 Min', // only 2 exercises but 4 phases
  BuildQuadSuperset4Min = 'Build Quad Superset 4 Min',
  BuildQuadSupersetQuicksetup4Min = 'Build Quad Superset Quicksetup 4 Min',
  BurnQuadSuperset3Min = 'Burn Quad Superset 3 Min',
  BurnQuadSupersetQuicksetup3Min = 'Burn Quad Superset Quicksetup 3 Min',
  HomeQuarterSetup = 'HomeQuarterSetup',
  BurnSingle90Sec = 'Burn Single 90 Sec',
  BuildSingle90Sec = 'Build Single 90 Sec',
  MotivBuildQ1 = 'Motiv Build Q1',
  MotivBuildQ2 = 'Motiv Build Q2',
  MotivBuildQ3 = 'Motiv Build Q3',
  MotivBurnQ1 = 'Motiv Burn Q1',
  MotivBurnQ2 = 'Motiv Burn Q2',
  MotivBurnQ3 = 'Motiv Burn Q3',

  Boxing = 'Boxing',
}

export enum MotivationalVoiceTipType {
  MotivQ1 = 'Motiv Q1',
  MotivQ2 = 'Motiv Q2',
  MotivQ3 = 'Motiv Q3',
}

type EquipmentSearchFunction = (equipment: Equipment[]) => boolean;

const resistanceFormats = [
  ExerciseFormat.Build1FlashCombos,
  ExerciseFormat.Build2AmrapSets,
  ExerciseFormat.Build3BoxingEndurance,
  ExerciseFormat.BuildTripple3Min,
  ExerciseFormat.BuildDoubleDouble3Min,
  ExerciseFormat.BuildDoubleDouble4Min,
  ExerciseFormat.BuildTripple3Min20Sec,
  ExerciseFormat.BuildQuadSuperset4Min,
  ExerciseFormat.BuildQuadSupersetQuicksetup4Min,
  ExerciseFormat.BuildDouble3Min,
  ExerciseFormat.BuildSingle90Sec,
];
export class Exercise {
  public static shrinkExercisesByRemovingHistoryAndInstructions(allExercises: Exercise[]): Exercise[] {
    return allExercises.map((e) => {
      const excopy: Exercise = JSON.parse(JSON.stringify(e));
      excopy.reviewHistory = '';
      excopy.reviewHistoryPU = '';
      excopy.instructions = '';
      return excopy;
    });
  }

  public static getCardioOrResistancePreferenceFromFormat(exerciseFormat: ExerciseFormat): CardioOrResistance {
    if (exerciseFormat === ExerciseFormat.Build1FlashCombos
     || exerciseFormat === ExerciseFormat.Build2AmrapSets
     || exerciseFormat === ExerciseFormat.Build3BoxingEndurance) {
      return CardioOrResistance.Resistance;
    }

    // default
    return CardioOrResistance.Cardio;
  }

  public static isHomeExerciseFormat(exerciseFormat: ExerciseFormat) {
    return exerciseFormat === ExerciseFormat.BurnTripple3Min
      || exerciseFormat === ExerciseFormat.BuildTripple3Min
      || exerciseFormat === ExerciseFormat.BurnTripple3Min20Sec
      || exerciseFormat === ExerciseFormat.BuildTripple3Min20Sec
      || exerciseFormat === ExerciseFormat.BurnDouble3Min
      || exerciseFormat === ExerciseFormat.BurnDoubleDouble3Min
      || exerciseFormat === ExerciseFormat.BuildDouble3Min
      || exerciseFormat === ExerciseFormat.BuildDoubleDouble3Min
      || exerciseFormat === ExerciseFormat.BuildDoubleDouble4Min
      || exerciseFormat === ExerciseFormat.BuildQuadSuperset4Min
      || exerciseFormat === ExerciseFormat.BuildQuadSupersetQuicksetup4Min
      || exerciseFormat === ExerciseFormat.BurnQuadSuperset3Min
      || exerciseFormat === ExerciseFormat.BurnQuadSupersetQuicksetup3Min
      || exerciseFormat === ExerciseFormat.BurnSingle90Sec
      || exerciseFormat === ExerciseFormat.BuildSingle90Sec
      || exerciseFormat === ExerciseFormat.HomeQuarterSetup
      || exerciseFormat === ExerciseFormat.HomeBalanceQuad
      || exerciseFormat === ExerciseFormat.WUModBalance
      || exerciseFormat === ExerciseFormat.WDBalance
      || exerciseFormat === ExerciseFormat.WULowBalance
      || exerciseFormat === ExerciseFormat.WUBalanceHybrid
      || exerciseFormat === ExerciseFormat.Boxing;
  }

  public static isStudioExerciseFormat(exerciseFormat: ExerciseFormat) {
    return exerciseFormat === ExerciseFormat.Build1FlashCombos
    || exerciseFormat === ExerciseFormat.Build2AmrapSets
    || exerciseFormat === ExerciseFormat.Build3BoxingEndurance
    || exerciseFormat === ExerciseFormat.Burn1FlashCombos
    || exerciseFormat === ExerciseFormat.Burn2Endurance
    || exerciseFormat === ExerciseFormat.HeroZonePunches
    || exerciseFormat === ExerciseFormat.BalanceFlashCombo
    || exerciseFormat === ExerciseFormat.Boxing;
  }

  public static isHomeBreak(exerciseFormat: ExerciseFormat | undefined) {
    return exerciseFormat === ExerciseFormat.HomeBreakBurn
    || exerciseFormat === ExerciseFormat.HomeBreakBuild
    || exerciseFormat === ExerciseFormat.HomeBreakBalance;
  }

  public static isCardioFormat(exerciseFormat: ExerciseFormat) {
    return !resistanceFormats.includes(exerciseFormat);
  }

  public static isResistanceFormat(exerciseFormat: ExerciseFormat) {
    return resistanceFormats.includes(exerciseFormat);
  }

  public static isBalanceFormat(exerciseFormat: ExerciseFormat) {
    return exerciseFormat === ExerciseFormat.HomeBalanceQuad
    || exerciseFormat === ExerciseFormat.BalanceFlashCombo
    || exerciseFormat === ExerciseFormat.WUModBalance
    || exerciseFormat === ExerciseFormat.WDBalance
    || exerciseFormat === ExerciseFormat.WULowBalance
    || exerciseFormat === ExerciseFormat.WDBalanceStudio
    || exerciseFormat === ExerciseFormat.WUBalanceHybrid
    || exerciseFormat === ExerciseFormat.MotiveBalanceQ1
    || exerciseFormat === ExerciseFormat.MotiveBalanceQ2
    || exerciseFormat === ExerciseFormat.MotiveBalanceQ3;
  }

  public static hasBuildAndBurnFormats(exerciseFormats: ExerciseFormat[]) {
    let hasResistanceFormat = false;
    let hasCardioFormat = false;
    for (const format of exerciseFormats) {
      if (Exercise.isResistanceFormat(format)) {
        hasResistanceFormat = true;
      } else if (Exercise.isCardioFormat(format)) {
        hasCardioFormat = true;
      }
      if (hasResistanceFormat && hasCardioFormat) {
        return true;
      }
    }
    return false;
  }

  public static getCardioOrResistancesFromFormat(exerciseFormat: ExerciseFormat): CardioOrResistance[] {
    const isBalanceFormat = Exercise.isBalanceFormat(exerciseFormat);
    const isCardioFormat = Exercise.isCardioFormat(exerciseFormat);
    const isResistanceFormat = Exercise.isResistanceFormat(exerciseFormat);

    if (isBalanceFormat) {
      return [CardioOrResistance.Balance];
    }

    if (isResistanceFormat) {
      return [CardioOrResistance.Resistance];
    }

    if (isCardioFormat) {
      return [CardioOrResistance.Cardio];
    }
    // default
    return [CardioOrResistance.Cardio, CardioOrResistance.Resistance];
  }

  public static isPauseFormat(exerciseFormat: ExerciseFormat | undefined) {
    return exerciseFormat === ExerciseFormat.PauseAtHalfwayBreak
        || exerciseFormat === ExerciseFormat.PauseAtWorkoutStarting
        || exerciseFormat === ExerciseFormat.HomeBreakBuild
        || exerciseFormat === ExerciseFormat.HomeBreakBurn
        || exerciseFormat === ExerciseFormat.HomeBreakBalance;
  }

  public static isMotivFormat(exerciseFormat: ExerciseFormat | undefined) {
    return exerciseFormat === ExerciseFormat.MotivBuildQ1
      || exerciseFormat === ExerciseFormat.MotivBuildQ2
      || exerciseFormat === ExerciseFormat.MotivBuildQ3
      || exerciseFormat === ExerciseFormat.MotivBurnQ1
      || exerciseFormat === ExerciseFormat.MotivBurnQ2
      || exerciseFormat === ExerciseFormat.MotivBurnQ3
      || exerciseFormat === ExerciseFormat.MotiveBalanceQ1
      || exerciseFormat === ExerciseFormat.MotiveBalanceQ2
      || exerciseFormat === ExerciseFormat.MotiveBalanceQ3;
  }

  public static isQuadFormat(exerciseFormat: ExerciseFormat | undefined) {
    return exerciseFormat === ExerciseFormat.BuildQuadSuperset4Min
    || exerciseFormat === ExerciseFormat.BuildQuadSupersetQuicksetup4Min
    || exerciseFormat === ExerciseFormat.BurnQuadSuperset3Min
    || exerciseFormat === ExerciseFormat.BurnQuadSupersetQuicksetup3Min;
  }

  public static compare3BodyGroup(a: string[], b: string[]): boolean {
    // full can be either all or nothing
    if ((a.length === 0 || a.length === 3) && (b.length === 0 || b.length === 3)) {
      return true;
    }

    return JSON.stringify(a.sort()) === JSON.stringify(b.sort());
  }

  public static get3BodyGroup(smallBodyGroup: SmallBodyGroupType): BigBodyGroup | undefined {
    return Exercise.map3BodyGroup.get(smallBodyGroup);
  }

  public static get3BodyGroups(smallBodyGroups: SmallBodyGroupType[]): BigBodyGroup[] {
    const groups = new Set<BigBodyGroup>();
    if (smallBodyGroups == null) {
      smallBodyGroups = [];
    }
    for (const smallBodyGroup of smallBodyGroups) {
      const threeBodyGroup = Exercise.get3BodyGroup(smallBodyGroup);
      if (threeBodyGroup != null) {
        groups.add(threeBodyGroup);
      }
    }
    return [...groups.values()];
  }

  public static getAllSmallBodyGroups() {
    return Array.from(Exercise.map3BodyGroup.keys());
  }

  public static getSmallBodyGroupsFor3BodyGroups(threeBodyGroups: string[]) {
    const smallBodyGroups = new Set<string>();
    const allSmallBodyGroups = Exercise.getAllSmallBodyGroups();
    allSmallBodyGroups.forEach((smallBodyGroup) => {
      const threeBodyGroup: any = Exercise.map3BodyGroup.get(smallBodyGroup);
      if (typeof threeBodyGroup === 'string') {
        if (threeBodyGroups.indexOf(threeBodyGroup) !== -1) {
          smallBodyGroups.add(smallBodyGroup);
        }
      }
    });

    return Array.from(smallBodyGroups);
  }

  public static forcedBodyGroupsForStudioArea(areaCategory: AreaCategory): string[] | undefined {
    const fullBody = [BigBodyGroup.BackAndArms, BigBodyGroup.Lower, BigBodyGroup.Upper];

    switch (areaCategory) {
      case 'treadmill':
      case 'bicycle':
      case 'step':
        return [BigBodyGroup.Lower];
      case 'elliptical':
      case 'rower':
      case 'calisthenic':
        return fullBody;
      case 'boxing':
        return fullBody;
      default:
        return undefined;
    }
  }

  public static getEquipmentForStudioArea(areaCategory: AreaCategory): Equipment[] {
    switch (areaCategory) {
      case 'calisthenic':
        return [Equipment.Bodyweight];
      case 'dumbbell':
        return [Equipment.Bodyweight, Equipment.Dumbbells, Equipment.Pushbells];
      case 'benchAndDumbbell':
        return [Equipment.Bodyweight, Equipment.Bench, Equipment.Dumbbells, Equipment.Pushbells];
      case 'boxing':
        return [Equipment.Bodyweight];
      default:
        throw new Error(`No equipment should be for area category ${areaCategory}`);
    }
  }

  public static getCurrentBodyGroupFrom3BodyGroups(threeBodyGroups: string[]) {
    if (threeBodyGroups.length === 0 || threeBodyGroups.length === 3) {
      return 'fullBody';
    }
    return threeBodyGroups[0];
  }

  public static getDescriptionFrom(bigBodyGroup: string): string {
    if (bigBodyGroup === BigBodyGroup.Upper) {
      return 'Upper body';
    }
    if (bigBodyGroup === BigBodyGroup.BackAndArms) {
      return 'Back & Arms';
    }
    if (bigBodyGroup === BigBodyGroup.Lower) {
      return 'Lower body';
    }
    return 'Full body';
  }

  public static getAdjustedEquipmentWeightLevel(
    exercise: Exercise,
    cardioOrResistance: CardioOrResistance,
  ): number | 'Bodyweight' {
    const weight = exercise.equipmentWeightLevel;
    const shouldNotLowerWeight = weight === 'Bodyweight'
      || !BaseEquipment.containsHomeBands(exercise.equipment)
      || !Exercise.hasBuildAndBurnFormats(exercise.exerciseFormats)
      || cardioOrResistance === CardioOrResistance.Resistance;
    if (shouldNotLowerWeight) {
      return weight;
    }
    return Math.max(weight - 1, 1);
  }

  public static getFormattedTips(exercise: Exercise): string[] {
    try {
      if (!exercise.tip || exercise.tip.length === 0) {
        return [];
      }

      const tips = exercise.tip.split('.').map((tip: string) => {
        const trimmedTip = tip.trim();
        return trimmedTip;
      });
      return tips;
    } catch (e) {
      return ['No available tips'];
    }
  }

  public static isRepeatingExercise(exercise: Exercise, alternateExercise: Exercise) {
    return exercise.uniqueReferenceNumber === alternateExercise.uniqueReferenceNumber;
  }

  public static isBodyWeightExercise(exercise: Exercise) {
    return exercise.equipment.indexOf(Equipment.Bodyweight) !== -1;
  }

  public static getTechniqueTipAvailInstructors(exercise: Exercise): string[] {
    return exercise.techniqueTipReviewStatuses ? exercise.techniqueTipReviewStatuses.reduce((acc: string[], cur) => {
      if (cur.passed) {
        acc.push(cur.ptName);
      }
      return acc;
    }, []) : [];
  }

  public static getPreviousExerciseWithEquipment(
    equipmentSearchFunc: EquipmentSearchFunction,
    userExerciseSessions?: UserExerciseSession[],
  ):
    Exercise | undefined {
    if (!userExerciseSessions) {
      return undefined;
    }
    const reversedSessions = userExerciseSessions.slice().reverse();
    for (const session of reversedSessions) {
      if (equipmentSearchFunc(session.exercise.equipment)) {
        return session.exercise;
      }
    }
    return undefined;
  }

  private static map3BodyGroup: Map<string, BigBodyGroup > = new Map<string, BigBodyGroup >([
    [SmallBodyGroup.Shoulders, BigBodyGroup.Upper],
    [SmallBodyGroup.Triceps, BigBodyGroup.Upper],
    [SmallBodyGroup.Chest, BigBodyGroup.Upper],
    [SmallBodyGroup.Abdomen, BigBodyGroup.Lower],
    [SmallBodyGroup.GlutesAndHips, BigBodyGroup.Lower],
    [SmallBodyGroup.UpperLegs, BigBodyGroup.Lower],
    [SmallBodyGroup.LowerLegs, BigBodyGroup.Lower],
    [SmallBodyGroup.Biceps, BigBodyGroup.BackAndArms],
    [SmallBodyGroup.UpperBackAndTraps, BigBodyGroup.BackAndArms],
    [SmallBodyGroup.LowerBackAndLats, BigBodyGroup.BackAndArms],
    [SmallBodyGroup.LowerArms, BigBodyGroup.BackAndArms],
  ]);

  public name: string;

  public groupName: string;

  public level: Level[];

  public bagContact: boolean;

  public cardioOrResistance: CardioOrResistance[];

  public bodyGroups: SmallBodyGroupType[];

  public compatibleZones: number[];

  public compatibleGymTypes: GymTypes[];

  public definedZones: number[];

  public similarityCode: number[];

  public instructions: string; // only for review purposes

  public reviewHistory: string; // only for review purposes

  public reviewHistoryPU: string; // only for review purposes

  public reviewPassed: boolean;

  public tip: string;

  public tipLocation: string;

  public tipVerbalInstruction: string;

  public techniqueTip?: string;

  public equipment: Equipment[] = [];

  public uniqueReferenceNumber: string;

  public exerciseFormats: ExerciseFormat[];

  public disable: boolean;

  public firstUsedSetNumber: number;

  public lastUsedSetNumber: number;

  public numberOfOccurances: number;

  public nonContactMirrorExerciseRef: string;

  public videoModel: string;

  public equipmentWeightLevel: number | 'Bodyweight';

  public voiceTipFileName?: string;

  public voiceTipReviewStatuses: IVoiceTipReviewStatus[];

  public techniqueTipReviewStatuses: IVoiceTipReviewStatus[];

  public homeBenchSetupOptions: BenchOption[];

  public homeBenchSetupPosition: any[];

  public homeBenchSetupAnchorPoint: BenchAnchorPoint[];

  public equipmentWeightInfo?: IEquipmentWeightInfo;

  public primaryMuscleMover: SmallBodyGroupType;

  // tslint:disable-next-line:variable-name
  public _rev: string;

  constructor(
    name: string,
    groupName: string,
    level: Level[],
    bagContact: boolean,
    cardioOrResistance: [CardioOrResistance],
    bodyGroups: SmallBodyGroupType[],
    compatibleZones: number[],
    similarityCode: number[],
    exerciseFormats: ExerciseFormat[] = [ExerciseFormat.Burn2Endurance],
  ) {
    this.name = name;
    this.groupName = groupName;
    this.level = level;
    this.bagContact = bagContact;
    this.cardioOrResistance = cardioOrResistance;
    this.bodyGroups = bodyGroups;
    this.compatibleZones = compatibleZones;
    this.similarityCode = similarityCode;
    this.exerciseFormats = exerciseFormats;
  }
}
